import React from "react";
import Cta from "@qlik/qlik/src/components/Cta";
import RichText from "@qlik/qlik/src/components/RichText";
import Media from "@qlik/qlik/src/components/Media";
import Link from "@qlik/qlik/src/components/Link";
import { getSpacingClassName } from "@qlik/qlik/src/lib/util/spacing";
import { getColorClassName } from "@qlik/qlik/src/lib/util/colors";
import * as styles from "./styles.module.css";
import { getLinkFromCta } from "@qlik/qlik/src/lib/util/helpers";

/**
 * CardPromo component.
 * Renders a promotional card with title, description, body, cta, and media.
 * @param {Object} props - The component props.
 * @param {Object} props.data - The data for the card.
 * @returns {JSX.Element} The rendered component.
 */
const CardPromo = ({ data }) => {
    const { customId, cardItem, backgroundColor, cardItemBackgroundColor, additionalConfiguration } = data ?? {};
    const { title, description, body, cta, media } = cardItem ?? {};

    // Determine the order of the media and text based on the additional configuration
    const orderMediaText = String(additionalConfiguration?.contentOrder) === "Media/Text";

    // Utility classes for order of media and text
    const mediaOrderClass = orderMediaText ? "md:flex-row" : "md:flex-row-reverse";

    const sectionSpacingClassName = getSpacingClassName(additionalConfiguration, "py-8 lg:py-20");
    const { sectionColorMapping, cardColorMapping } = getColorClassName(backgroundColor, cardItemBackgroundColor);

    const cardLink = getLinkFromCta(cta);

    return (
        <section
            id={customId}
            data-section
            className={`promoCard card-promo scroll-mt-45 ${sectionSpacingClassName} ${sectionColorMapping.bgColor}`}
        >
            <div className="container">
                <div
                    className={`card-wrapper rounded md:rounded-sm overflow-hidden shadow-xxlMedium md:flex ${mediaOrderClass}`}
                >
                    {media?.[0] && (
                        <div className="md:w-1/2 md:relative md:flex-none">
                            {media?.[0] &&
                                (cardLink ? (
                                    <Link to={cardLink}>
                                        <Media
                                            media={media?.[0]}
                                            className="w-full h-55 md:h-full object-cover md:absolute hover:cursor-pointer"
                                        />
                                    </Link>
                                ) : (
                                    <Media
                                        media={media?.[0]}
                                        className="w-full h-55 md:h-full object-cover md:absolute"
                                    />
                                ))}
                        </div>
                    )}
                    <div
                        className={`${cardColorMapping?.bgColor ||
                            "bg-white"} md:w-1/2 px-5 pt-10 pb-5 md:p-8 lg:p-15 lg:pt-15.25 rounded-b md:rounded-br-none md:rounded-l-sm`}
                    >
                        {/* Render title if it exists */}
                        {title && (
                            <h3 className={`${cardColorMapping?.primaryTextColor} title-5 text-xl lg:title-3 mb-3.5`}>
                                {title}
                            </h3>
                        )}

                        {/* Render description if it exists */}
                        {description?.description && (
                            <p className={`${cardColorMapping?.primaryTextColor}`}>{description.description}</p>
                        )}

                        {/* Render body if it exists */}
                        {body && <RichText data={cardItem} className={`${styles.bodyContent}`} />}
                        {/* Render cta if it exists */}
                        {cta && (
                            <div className="col-12 mt-3.5 md:mt-3.75 flex gap-2 justify-start flex-wrap">
                                {cta.map((button) => (
                                    <Cta key={button.id} data={button} className="tracking-normal" />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default CardPromo;
